export type ChatwootAttributeValue = string | number | boolean | null
export type ChatwootAttributes = Record<string, ChatwootAttributeValue>

export interface ChatwootUser {
  email?: string
  name?: string
  avatar_url?: string
  identifier_hash?: string
}

interface ChatwootApi {
  setUser?(identifier: string, user: ChatwootUser): void
  setCustomAttributes(attributes: ChatwootAttributes): void
  setConversationCustomAttributes?(attributes: ChatwootAttributes): void
  toggleBubbleVisibility?(visibility: 'show' | 'hide'): void
  toggle?(state?: 'open' | 'close'): void
  popoutChatWindow?(): void
}

interface ChatwootSdk {
  run(options: { websiteToken: string; baseUrl: string }): void
}

interface ChatwootWindow extends EventTarget {
  $chatwoot?: ChatwootApi
  chatwootSDK?: ChatwootSdk
  chatwootSettings?: Record<string, unknown>
  setTimeout(handler: () => void, timeout: number): number
  clearTimeout(timeoutId: number): void
}

interface ChatwootDocument {
  createElement(tagName: 'script'): HTMLScriptElement
  head: Pick<HTMLHeadElement, 'appendChild'>
}

export interface ChatwootClientOptions {
  baseUrl?: string
  websiteToken?: string
  settings?: Record<string, unknown>
  readinessTimeoutMs?: number
  runtime?: {
    window: ChatwootWindow
    document: ChatwootDocument
  }
}

const scriptLoads = new WeakMap<object, Map<string, Promise<boolean>>>()

function isSdk(value: unknown): value is ChatwootSdk {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as ChatwootSdk).run === 'function',
  )
}

function isApi(value: unknown): value is ChatwootApi {
  if (!value || typeof value !== 'object') return false
  const api = value as ChatwootApi
  return typeof api.setCustomAttributes === 'function'
}

function loadSdkScript(
  runtimeWindow: ChatwootWindow,
  runtimeDocument: ChatwootDocument,
  scriptUrl: string,
  timeoutMs: number,
): Promise<boolean> {
  if (isSdk(runtimeWindow.chatwootSDK)) return Promise.resolve(true)

  let documentLoads = scriptLoads.get(runtimeDocument)
  if (!documentLoads) {
    documentLoads = new Map()
    scriptLoads.set(runtimeDocument, documentLoads)
  }

  const existingLoad = documentLoads.get(scriptUrl)
  if (existingLoad) return existingLoad

  const load = new Promise<boolean>(resolve => {
    const script = runtimeDocument.createElement('script')
    const timer = runtimeWindow.setTimeout(() => resolve(false), timeoutMs)
    const finish = (loaded: boolean) => {
      runtimeWindow.clearTimeout(timer)
      resolve(loaded)
    }

    script.src = scriptUrl
    script.async = true
    script.defer = true
    script.onload = () => finish(true)
    script.onerror = () => finish(false)
    runtimeDocument.head.appendChild(script)
  })

  documentLoads.set(scriptUrl, load)
  void load.then(loaded => {
    if (!loaded) documentLoads.delete(scriptUrl)
  })
  return load
}

export class ChatwootClient {
  private readonly baseUrl: string
  private readonly websiteToken: string
  private readonly readinessTimeoutMs: number
  private readonly settings: Record<string, unknown>
  private readonly runtimeWindow?: ChatwootWindow
  private readonly runtimeDocument?: ChatwootDocument
  private initialization?: Promise<boolean>

  public constructor(options: ChatwootClientOptions) {
    this.baseUrl = options.baseUrl?.replace(/\/$/, '') ?? ''
    this.websiteToken = options.websiteToken?.trim() ?? ''
    this.readinessTimeoutMs = options.readinessTimeoutMs ?? 10_000
    this.settings = options.settings ?? {}
    this.runtimeWindow =
      options.runtime?.window ??
      (typeof window !== 'undefined' ? (window as ChatwootWindow) : undefined)
    this.runtimeDocument =
      options.runtime?.document ??
      (typeof document !== 'undefined'
        ? (document as unknown as ChatwootDocument)
        : undefined)
  }

  public initialize(): Promise<boolean> {
    if (!this.initialization) {
      this.initialization = this.initializeOnce().then(success => {
        if (!success) this.initialization = undefined
        return success
      })
    }
    return this.initialization
  }

  public setUser(identifier: string, user: ChatwootUser): Promise<boolean> {
    if (!identifier.trim()) return Promise.resolve(false)
    return this.perform(api => {
      if (!api.setUser) return false
      api.setUser(identifier, user)
      return true
    })
  }

  public setContactAttributes(
    attributes: ChatwootAttributes,
  ): Promise<boolean> {
    return this.perform(api => {
      api.setCustomAttributes(attributes)
      return true
    })
  }

  public setConversationAttributes(
    attributes: ChatwootAttributes,
  ): Promise<boolean> {
    return this.perform(api => {
      if (!api.setConversationCustomAttributes) return false
      api.setConversationCustomAttributes(attributes)
      return true
    })
  }

  public hide(): Promise<boolean> {
    return this.perform(api => {
      if (!api.toggleBubbleVisibility) return false
      api.toggleBubbleVisibility('hide')
      return true
    })
  }

  public show(): Promise<boolean> {
    return this.perform(api => {
      if (!api.toggleBubbleVisibility) return false
      api.toggleBubbleVisibility('show')
      return true
    })
  }

  public open(): Promise<boolean> {
    return this.perform(api => {
      if (!api.toggle) return false
      api.toggle('open')
      return true
    })
  }

  public close(): Promise<boolean> {
    return this.perform(api => {
      if (!api.toggle) return false
      api.toggle('close')
      return true
    })
  }

  public toggle(): Promise<boolean> {
    return this.perform(api => {
      if (!api.toggle) return false
      api.toggle()
      return true
    })
  }

  public popout(): Promise<boolean> {
    return this.perform(api => {
      if (!api.popoutChatWindow) return false
      api.popoutChatWindow()
      return true
    })
  }

  private async initializeOnce(): Promise<boolean> {
    const runtimeWindow = this.runtimeWindow
    const runtimeDocument = this.runtimeDocument
    if (
      !this.baseUrl ||
      !this.websiteToken ||
      !runtimeWindow ||
      !runtimeDocument
    ) {
      return false
    }

    runtimeWindow.chatwootSettings = {
      ...runtimeWindow.chatwootSettings,
      ...this.settings,
    }

    const loaded = await loadSdkScript(
      runtimeWindow,
      runtimeDocument,
      `${this.baseUrl}/packs/js/sdk.js`,
      this.readinessTimeoutMs,
    )
    if (!loaded || !isSdk(runtimeWindow.chatwootSDK)) return false

    const ready = this.waitForReady(runtimeWindow)
    try {
      runtimeWindow.chatwootSDK.run({
        websiteToken: this.websiteToken,
        baseUrl: this.baseUrl,
      })
    } catch {
      ready.cancel()
      return false
    }

    const api = await ready.result
    if (!api) return false

    return true
  }

  private waitForReady(runtimeWindow: ChatwootWindow): {
    result: Promise<ChatwootApi | null>
    cancel: () => void
  } {
    if (isApi(runtimeWindow.$chatwoot)) {
      return {
        result: Promise.resolve(runtimeWindow.$chatwoot),
        cancel: () => undefined,
      }
    }

    let cancel: () => void = () => undefined
    const result = new Promise<ChatwootApi | null>(resolve => {
      const finish = (api: ChatwootApi | null) => {
        runtimeWindow.clearTimeout(timer)
        runtimeWindow.removeEventListener('chatwoot:ready', onReady)
        resolve(api)
      }
      cancel = () => finish(null)
      const onReady = () => {
        if (isApi(runtimeWindow.$chatwoot)) finish(runtimeWindow.$chatwoot)
      }
      const timer = runtimeWindow.setTimeout(
        () => finish(null),
        this.readinessTimeoutMs,
      )
      runtimeWindow.addEventListener('chatwoot:ready', onReady)
    })
    return { result, cancel }
  }

  private async perform(
    operation: (api: ChatwootApi) => boolean,
  ): Promise<boolean> {
    if (!(await this.initialize())) return false
    const api = this.runtimeWindow?.$chatwoot
    if (!isApi(api)) return false

    try {
      return operation(api)
    } catch {
      return false
    }
  }
}
